/**
 * Génération de la description et des tags YouTube d'une vidéo d'exercice.
 * Fonctions PURES (testées) — réutilisées par l'UI (pré-remplissage de l'éditeur)
 * et par le worker (valeur finale envoyée à l'API YouTube).
 */

/** Site par défaut (les liens d'exercice pointent vers l'application). */
export const DEFAULT_APP_URL = 'https://jeraisonne.fr';

export interface DescriptionExercise {
  titre: string;
  /** id de l'exercice → lien `{appUrl}/demo/exercise/{id}`. Omis si absent. */
  exerciseId?: string | null;
}

export interface DescriptionInput {
  /** Niveau global de la vidéo (ex. « 3ème »). */
  niveau: string;
  /** Matière (ex. « maths »). Optionnelle. */
  matiere?: string | null;
  /** Liste des exercices de la vidéo (playlist, 1..N). */
  exercises: DescriptionExercise[];
  /** Base URL du site — défaut {@link DEFAULT_APP_URL}. */
  appUrl?: string | null;
  /** Crédit musique — optionnel. Si `artiste` absent, le bloc 🎵 est omis. */
  artiste?: string | null;
  licence?: string | null;
  url?: string | null;
}

/** Lien application vers un exercice précis (route démo anonyme). */
export function exerciseAppUrl(exerciseId: string, appUrl = DEFAULT_APP_URL): string {
  return `${appUrl.replace(/\/$/, '')}/demo/exercise/${exerciseId}`;
}

/**
 * Construit la description YouTube selon le gabarit produit :
 *
 *   📚 Exercices interactifs JeRaisonne
 *
 *   3 exercices · Niveau : 3ème · Maths
 *
 *   👉 Réviser sur l'application :
 *   • La pyramide de Khéops
 *     https://jeraisonne.fr/demo/exercise/…
 *   • …
 *
 *   📱 Découvrir JeRaisonne :
 *   https://jeraisonne.fr
 *
 *   🎵 Musique : artiste / licence / url   (si renseigné)
 */
export function buildYoutubeDescription(input: DescriptionInput): string {
  const appUrl = (input.appUrl?.trim() || DEFAULT_APP_URL).replace(/\/$/, '');
  const exercises = input.exercises.filter((e) => e.titre && e.titre.trim());
  const count = exercises.length;
  const noun = count > 1 ? 'exercices' : 'exercice';

  const metaParts = [`${count} ${noun}`, `Niveau : ${input.niveau}`];
  if (input.matiere && input.matiere.trim()) metaParts.push(capitalize(input.matiere.trim()));

  const lines: string[] = [
    `📚 ${count > 1 ? 'Exercices interactifs' : 'Exercice interactif'} JeRaisonne`,
    '',
    metaParts.join(' · '),
    '',
    "👉 Réviser sur l'application :",
  ];

  for (const ex of exercises) {
    lines.push(`• ${ex.titre.trim()}`);
    if (ex.exerciseId && ex.exerciseId.trim()) {
      lines.push(`  ${exerciseAppUrl(ex.exerciseId.trim(), appUrl)}`);
    }
  }

  lines.push('', '📱 Découvrir JeRaisonne :', appUrl);

  if (input.artiste && input.artiste.trim()) {
    lines.push('', '🎵 Musique :', input.artiste.trim());
    if (input.licence && input.licence.trim()) lines.push(input.licence.trim());
    if (input.url && input.url.trim()) lines.push(input.url.trim());
  }

  return lines.join('\n');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Construit une liste de tags YouTube dédupliquée et bornée (50 max, limite
 * YouTube). Combine des tags de base avec niveau / matière / chapitre nettoyés.
 */
export function buildYoutubeTags(input: {
  niveau?: string | null;
  matiere?: string | null;
  chapitre?: string | null;
  extra?: string[];
}): string[] {
  const base = [
    'JeRaisonne',
    'révisions',
    'maths',
    'exercice',
    'collège',
    'lycée',
    'brevet',
    'bac',
  ];
  const dynamic = [input.niveau, input.matiere, input.chapitre, ...(input.extra ?? [])]
    .filter((t): t is string => Boolean(t && t.trim()))
    .map((t) => t.trim());

  const seen = new Set<string>();
  const out: string[] = [];
  for (const tag of [...dynamic, ...base]) {
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= 50) break;
  }
  return out;
}

/** Titre YouTube par défaut « {titre} — {niveau} | JeRaisonne » borné à 100 car. */
export function buildYoutubeTitle(input: { titre: string; niveau?: string | null }): string {
  const suffix = input.niveau ? ` — ${input.niveau}` : '';
  const full = `${input.titre}${suffix} | JeRaisonne`;
  return full.length <= 100 ? full : full.slice(0, 99).trimEnd() + '…';
}
